import { useEffect, useState } from "react";

import { Query, useIsFetching, useQueryClient } from "@tanstack/react-query";
import { isFunction } from "lodash-es";

import { casesKeys } from "src/cases/queries/cases";
import { PREFIX_CACHE_KEY_LIST } from "src/eventsCatalogue/queries";
import { useCapabilities } from "src/hooks/useCapabilities";
import { useEnvironment } from "src/hooks/useEnvironment";
import { useIsCaseManagementEnabled } from "src/hooks/useIsCaseManagementEnabled";
import { useWorkspaceContext } from "src/router/routerContextHooks";
import { EntityViewParams } from "src/router/urls";
import { useParamsDecode } from "src/utils/useParamsDecode";

export enum EntityTabIds {
  Cases = "cases",
  Decisions = "decisions",
  Events = "events",
  Features = "features",
  RelatedEntities = "related-entities",
}

export const useDefaultEntityTab = (): EntityTabIds => {
  const { isEnabled: isCaseManagementEnabled } = useIsCaseManagementEnabled();
  const capabilities = useCapabilities();

  return isCaseManagementEnabled && capabilities.cases.canAccess
    ? EntityTabIds.Cases
    : EntityTabIds.Decisions;
};

export const decisionsQueryKeys = [
  ["flows"],
  ["decision", "v3"],
  ["decision-overview"],
];

export const useEntityTabRefresh = (activeTab: EntityTabIds) => {
  const [isManualRefresh, setIsManualRefresh] = useState(false); // state to differentiate between manual refresh triggered by user and background refreshes/initial mount fetch
  const queryClient = useQueryClient();
  const { id: entityId, schema } = useParamsDecode<EntityViewParams>();
  const [environment] = useEnvironment();
  const { workspace } = useWorkspaceContext();

  const queryKeysMappings: Record<
    EntityTabIds,
    (readonly unknown[] | ((query: Query) => boolean))[]
  > = {
    // Sort-less prefix so every sort variant is refreshed together — the Cases
    // table (active sort) and the tab count pill (default sort) share it.
    cases: [
      [...casesKeys.relatedEntity(workspace.id, environment, schema, entityId)],
    ],
    events: [
      ({ queryKey }) =>
        queryKey.includes(PREFIX_CACHE_KEY_LIST) &&
        queryKey.includes(workspace.id) &&
        queryKey.includes(entityId) &&
        queryKey.includes(environment),
    ],
    decisions: decisionsQueryKeys,
    features: [["features", workspace.id, environment, entityId, schema]],
    "related-entities": [["relatedEntities", schema]],
  };

  const queryKey = queryKeysMappings[activeTab];
  const filter = isFunction(queryKey[0])
    ? { predicate: queryKey[0] }
    : { queryKey: queryKey[0] };

  const isFetching = useIsFetching({
    ...filter,
    exact: false,
  });

  useEffect(() => {
    if (!isFetching && isManualRefresh) {
      setIsManualRefresh(false);
    }
  }, [isFetching, isManualRefresh]);

  const refresh = () => {
    setIsManualRefresh(true);
    queryKey.forEach((key) => {
      if (isFunction(key)) {
        queryClient.invalidateQueries({ predicate: key });
      } else {
        queryClient.invalidateQueries({ queryKey: key });
      }
    });
  };

  return {
    isRefreshing: isManualRefresh,
    refresh,
  };
};
